import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle, Send } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const AdminRequestDetail = () => {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const response = await api.get(`/admin/requests/${id}`);
      setRequest(response.data);
      setStatus(response.data.status);
      setAdminNotes(response.data.admin_notes || '');
    } catch (error) {
      toast.error('Failed to load request');
      navigate('/admin/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await api.put(`/admin/requests/${id}`, {
        status,
        admin_notes: adminNotes
      });
      toast.success('Request updated successfully');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (s) => {
    switch (s) {
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'denied': return 'bg-red-100 text-red-700';
      case 'more_info': return 'bg-blue-100 text-blue-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8" data-testid="admin-request-detail-page">
        <button
          onClick={() => navigate('/admin/requests')}
          className="mb-6 text-slate-600 hover:text-emerald-600 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Requests
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Request Details */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-800 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-white">Request #{request?.id}</h1>
                  <p className="text-slate-300 text-sm mt-1">
                    From: {request?.company_name || 'Unknown'} • {new Date(request?.submitted_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request?.status)}`}>
                  {request?.status?.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="p-6">
              <h3 className="font-bold text-slate-800 mb-4">Submission Details</h3>
              <div className="space-y-4">
                {request?.answers?.map((answer, idx) => (
                  <div key={idx} className="border-b border-slate-100 pb-4 last:border-0">
                    <p className="text-sm font-medium text-slate-500 mb-1">{answer.question_text}</p>
                    <p className="text-slate-800">{answer.answer_value || '-'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 h-fit">
            <h3 className="font-bold text-slate-800 mb-4">Update Status</h3>
            <form onSubmit={handleUpdateStatus} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="more_info">Request More Info</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="Add notes for the user..."
                />
              </div>

              <button
                type="submit"
                disabled={updating}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {updating ? 'Updating...' : 'Update Request'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminRequestDetail;
